import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRevenue } from '@/api/finance.api';
import { formatCurrencyEGP } from '@/lib/format';
import { FINANCE_PERIODS, type FinancePeriod, type RevenueResponse } from '@/types/shared';
import './FinancialReportPage.css';

/** A P&L "band": an income stream, a cost stream, or one that has both (e.g. exams). */
interface Band {
  key: string;
  income: string; // EGP decimal-as-string; '0.00' when the band has no income side
  cost: string; // likewise for the cost side
}

const toCents = (v: string): number => Math.round(Number(v) * 100);
const fromCents = (c: number): string => (c / 100).toFixed(2);

function bandsFromRevenue(r: RevenueResponse): Band[] {
  return [
    { key: 'tuition', income: r.income.tuitionPaid, cost: '0.00' },
    { key: 'downPayments', income: r.income.downPayments, cost: '0.00' },
    { key: 'exams', income: r.income.examPayments, cost: r.expenses.examCosts },
    { key: 'schoolCosts', income: '0.00', cost: r.expenses.schoolCosts },
    { key: 'referralCosts', income: '0.00', cost: r.expenses.referralCosts },
    { key: 'otherExpenses', income: '0.00', cost: r.expenses.otherExpenses },
  ];
}

/**
 * Financial report (`/reports/finance`, Admin only, 2026-07). A profit-and-loss summary over a
 * chosen Cairo period: total income, total costs, net profit, and a per-band breakdown (each income
 * and cost band with its earned / cost / net — e.g. "Exams: earned 40k, cost 20k, net 20k").
 * Reads the existing `GET /finance/revenue` (no new endpoint — it already returns every band).
 */
export function FinancialReportPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [period, setPeriod] = useState<FinancePeriod>('this-month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [revenue, setRevenue] = useState<RevenueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (period === 'custom' && (!from || !to)) return;
    setLoading(true);
    setError(null);
    getRevenue(period === 'custom' ? { period, from, to } : { period })
      .then(setRevenue)
      .catch(() => setError(t('financeReport.loadError')))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const money = (v: string) => formatCurrencyEGP(Number(v), lang);
  const bands = revenue ? bandsFromRevenue(revenue) : [];

  return (
    <section className="fa-fin-report">
      <div className="fa-fin-report__header">
        <div>
          <h1 className="fa-fin-report__title">{t('financeReport.pageTitle')}</h1>
          <p className="fa-fin-report__description">{t('financeReport.pageDescription')}</p>
        </div>
        <div className="fa-fin-report__periods" role="group" aria-label={t('financeReport.period')}>
          {FINANCE_PERIODS.map((p) => (
            <button
              key={p}
              type="button"
              className={`fa-btn ${period === p ? 'fa-btn--primary' : 'fa-btn--ghost'}`}
              aria-pressed={period === p}
              onClick={() => setPeriod(p)}
            >
              {t(`financeReport.periods.${p}`)}
            </button>
          ))}
        </div>
      </div>

      {period === 'custom' && (
        <div className="fa-fin-report__custom">
          <label className="fa-field">
            <span className="fa-field__label">{t('financeReport.from')}</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="fa-field">
            <span className="fa-field__label">{t('financeReport.to')}</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
      )}

      {loading ? (
        <p className="fa-fin-report__muted">{t('common.loading')}</p>
      ) : error ? (
        <p className="fa-fin-report__error">{error}</p>
      ) : revenue ? (
        <>
          <div className="fa-fin-report__cards">
            <div className="fa-fin-report__card fa-fin-report__card--income">
              <span className="fa-fin-report__card-label">{t('financeReport.totalIncome')}</span>
              <span className="fa-fin-report__card-value">{money(revenue.income.total)}</span>
            </div>
            <div className="fa-fin-report__card fa-fin-report__card--cost">
              <span className="fa-fin-report__card-label">{t('financeReport.totalCosts')}</span>
              <span className="fa-fin-report__card-value">{money(revenue.expenses.total)}</span>
            </div>
            <div
              className={`fa-fin-report__card ${
                toCents(revenue.net) >= 0 ? 'fa-fin-report__card--net' : 'fa-fin-report__card--loss'
              }`}
            >
              <span className="fa-fin-report__card-label">{t('financeReport.netProfit')}</span>
              <span className="fa-fin-report__card-value">{money(revenue.net)}</span>
            </div>
          </div>

          <div className="fa-fin-report__table-wrap">
            <table className="fa-fin-report__table">
              <thead>
                <tr>
                  <th>{t('financeReport.band')}</th>
                  <th className="fa-fin-report__num">{t('financeReport.earned')}</th>
                  <th className="fa-fin-report__num">{t('financeReport.cost')}</th>
                  <th className="fa-fin-report__num">{t('financeReport.net')}</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b) => {
                  const net = toCents(b.income) - toCents(b.cost);
                  return (
                    <tr key={b.key}>
                      <td>{t(`financeReport.bands.${b.key}`)}</td>
                      <td className="fa-fin-report__num">
                        {toCents(b.income) > 0 ? money(b.income) : '—'}
                      </td>
                      <td className="fa-fin-report__num">
                        {toCents(b.cost) > 0 ? money(b.cost) : '—'}
                      </td>
                      <td className={`fa-fin-report__num ${net < 0 ? 'fa-fin-report__neg' : ''}`}>
                        {money(fromCents(net))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th>{t('financeReport.total')}</th>
                  <td className="fa-fin-report__num">{money(revenue.income.total)}</td>
                  <td className="fa-fin-report__num">{money(revenue.expenses.total)}</td>
                  <td
                    className={`fa-fin-report__num ${
                      toCents(revenue.net) < 0 ? 'fa-fin-report__neg' : ''
                    }`}
                  >
                    {money(revenue.net)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}
